import React, { useState, useRef, useEffect, useCallback } from 'react';
import Calculator from '../Calculator/Calculator';
import FileExplorer from '../FileExplorer/FileExplorer';
import MusicPlayer from '../MusicPlayer/MusicPlayer';
import Notepad from '../Notepad/Notepad';
import YouTube from '../YouTube/YouTube';
import Terminal from '../Terminal/Terminal';
import Store from '../Store/Store';
import Browser from '../Browser/Browser';
import Settings from '../Settings/Settings';
import SettingsPlaceholder from '../Settings/SettingsPlaceholder';
import CodeEditor from '../CodeEditor/CodeEditor';
import NotificationCenter from '../Notifications/NotificationCenter';
import TicTacToe from '../TicTacToe/TicTacToe';
import AIAssistant from '../AIAssistant/AIAssistant';
import Clock from '../Clock/Clock';

// Enhanced WebBrowser with tabbed browsing
const WebBrowser = ({ url }) => {
  // List of websites known to work in iframes
  const safeWebsites = [
    { name: "DuckDuckGo", url: "https://duckduckgo.com" },
    { name: "Wikipedia", url: "https://en.m.wikipedia.org" },
    { name: "Mojeek", url: "https://www.mojeek.com/lite/" },
    { name: "Brave Search", url: "https://search.brave.com" },
    { name: "Bing", url: "https://www.bing.com" },
    { name: "GitHub", url: "https://github.com" },
    { name: "HTML5 Test", url: "https://html5test.com" },
    { name: "Hacker News", url: "https://news.ycombinator.com" },
    { name: "JSFiddle", url: "https://jsfiddle.net" },
    { name: "CodePen", url: "https://codepen.io" }
  ];

  const initialUrl = url || "duckduckgo://search";
  
  // Add browsing history for each tab
  const [tabsHistory, setTabsHistory] = useState({});
  const [tabs, setTabs] = useState([
    { id: 1, url: initialUrl, title: 'DuckDuckGo Search', favicon: null, loading: false, error: null, isCustomSearch: true }
  ]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [inputUrl, setInputUrl] = useState(initialUrl);
  const [nextTabId, setNextTabId] = useState(2);
  const [showSafeWebsites, setShowSafeWebsites] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);

  // Initialize history for tabs
  useEffect(() => {
    // Setup initial history for the tab if it doesn't exist yet
    setTabsHistory(prev => {
      const currentHistory = prev[activeTabId];
      if (!currentHistory) {
        const activeTab = tabs.find(tab => tab.id === activeTabId);
        if (activeTab) {
          return {
            ...prev,
            [activeTabId]: {
              stack: [activeTab.url],
              position: 0
            }
          };
        }
      }
      return prev;
    });
  }, [activeTabId, tabs]);

  // Update tab history when navigating
  const updateHistory = (tabId, url) => {
    setTabsHistory(prev => {
      const history = prev[tabId] || { stack: [], position: -1 };
      
      // If we're not at the end of the history, truncate forward history
      const newStack = history.position < history.stack.length - 1
        ? history.stack.slice(0, history.position + 1)
        : history.stack;
      
      // Add the new URL if it's different from the current one
      const updatedStack = history.position >= 0 && history.stack[history.position] === url
        ? newStack
        : [...newStack, url];
      
      return {
        ...prev,
        [tabId]: {
          stack: updatedStack,
          position: updatedStack.length - 1
        }
      };
    });
  };

  // Navigation functions
  const goBack = () => {
    const history = tabsHistory[activeTabId];
    if (!history || history.position <= 0) return;
    
    const newPosition = history.position - 1;
    const previousUrl = history.stack[newPosition];
    
    // Update tab without adding to history
    const isCustomSearch = previousUrl.startsWith('duckduckgo://search');
    updateTab(activeTabId, {
      url: previousUrl,
      loading: !isCustomSearch,
      error: null,
      isCustomSearch
    });
    
    setInputUrl(previousUrl);
    
    // Handle search URLs
    if (isCustomSearch) {
      const queryParam = new URLSearchParams(previousUrl.replace('duckduckgo://search', '')).get('q');
      if (queryParam) {
        setSearchQuery(queryParam);
        performDuckDuckGoSearch(queryParam);
      } else {
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    
    // Update position without adding to stack
    setTabsHistory(prev => ({
      ...prev,
      [activeTabId]: {
        ...prev[activeTabId],
        position: newPosition
      }
    }));
  };
  
  const goForward = () => {
    const history = tabsHistory[activeTabId];
    if (!history || history.position >= history.stack.length - 1) return;
    
    const newPosition = history.position + 1;
    const nextUrl = history.stack[newPosition];
    
    // Update tab without adding to history
    const isCustomSearch = nextUrl.startsWith('duckduckgo://search');
    updateTab(activeTabId, {
      url: nextUrl,
      loading: !isCustomSearch,
      error: null,
      isCustomSearch
    });
    
    setInputUrl(nextUrl);
    
    // Handle search URLs
    if (isCustomSearch) {
      const queryParam = new URLSearchParams(nextUrl.replace('duckduckgo://search', '')).get('q');
      if (queryParam) {
        setSearchQuery(queryParam);
        performDuckDuckGoSearch(queryParam);
      } else {
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    
    // Update position without adding to stack
    setTabsHistory(prev => ({
      ...prev,
      [activeTabId]: {
        ...prev[activeTabId],
        position: newPosition
      }
    }));
  };
  
  // Functions to check if can go back/forward
  const canGoBack = () => {
    const history = tabsHistory[activeTabId];
    return history && history.position > 0;
  };
  
  const canGoForward = () => {
    const history = tabsHistory[activeTabId];
    return history && history.position < history.stack.length - 1;
  };

  // Update active tab's URL in the address bar when switching tabs
  useEffect(() => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    if (activeTab) {
      setInputUrl(activeTab.url);
      
      // If it's a search URL, extract the query
      if (activeTab.url.startsWith('duckduckgo://search')) {
        const queryParam = new URLSearchParams(activeTab.url.replace('duckduckgo://search', '')).get('q');
        if (queryParam) {
          setSearchQuery(queryParam);
          performDuckDuckGoSearch(queryParam);
        }
      }
    }
  }, [activeTabId, tabs]);

  // Add focus and selection for centered search input
  useEffect(() => {
    // Focus the centered search input on initial page load
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    if (activeTab && activeTab.isCustomSearch && !searchQuery) {
      setTimeout(() => {
        const searchInput = document.getElementById('centered-search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [activeTabId, tabs, searchQuery]);

  // Perform DuckDuckGo API search
  const performDuckDuckGoSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    try {
      // List of CORS proxies to try
      const corsProxies = [
        "", // Try direct first
        "https://api.allorigins.win/raw?url=",
        "https://cors-anywhere.herokuapp.com/",
        "https://corsproxy.io/?",
      ];
      
      // Encode the base URL
      const ddgUrl = `https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1&no_redirect=1&skip_disambig=1`;
      
      let response = null;
      let proxyUsed = "";
      let error = null;
      
      // Try each proxy until one works
      for (const proxy of corsProxies) {
        try {
          const apiUrl = proxy + encodeURIComponent(ddgUrl);
          console.log(`Trying with proxy: ${proxy || 'No proxy'}`);
          
          // Using fetch with a timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          
          const resp = await fetch(proxy ? apiUrl : ddgUrl, { 
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          clearTimeout(timeoutId);
          
          if (resp.ok) {
            response = resp;
            proxyUsed = proxy;
            break;
          }
        } catch (e) {
          console.log(`Error with proxy ${proxy || 'No proxy'}:`, e);
          error = e;
        }
      }
      
      if (!response) {
        throw new Error(error ? error.message : "All proxies failed");
      }
      
      console.log(`Successfully fetched with ${proxyUsed || 'No proxy'}`);
      
      const data = await response.json();
      console.log('Search results received:', data);
      
      // Process and format the results
      const formattedResults = [];
      
      // Add the main abstract result if available
      if (data.Abstract) {
        formattedResults.push({
          type: 'abstract',
          title: data.Heading || query,
          content: data.Abstract,
          url: data.AbstractURL,
          source: data.AbstractSource
        });
      }
      
      // Add related topics
      if (data.RelatedTopics && data.RelatedTopics.length > 0) {
        data.RelatedTopics.forEach(topic => {
          if (topic.Result) {
            // Extract title and cleaned text from HTML
            const titleMatch = topic.Result.match(/<a[^>]*>(.*?)<\/a>/);
            const title = titleMatch ? titleMatch[1] : 'Related Topic';
            
            // Extract clean text (remove HTML tags)
            let cleanText = topic.Text || '';
            if (!cleanText && topic.Result) {
              cleanText = topic.Result.replace(/<[^>]*>/g, '');
            }
            
            formattedResults.push({
              type: 'topic',
              title: title,
              content: cleanText,
              url: topic.FirstURL,
              icon: topic.Icon?.URL
            });
          } else if (topic.Topics) {
            // Handle nested topics
            topic.Topics.forEach(subTopic => {
              const titleMatch = subTopic.Result.match(/<a[^>]*>(.*?)<\/a>/);
              const title = titleMatch ? titleMatch[1] : 'Related Topic';
              
              // Extract clean text
              let cleanText = subTopic.Text || '';
              if (!cleanText && subTopic.Result) {
                cleanText = subTopic.Result.replace(/<[^>]*>/g, '');
              }
              
              formattedResults.push({
                type: 'topic',
                title: title,
                content: cleanText,
                url: subTopic.FirstURL,
                icon: subTopic.Icon?.URL,
                category: topic.Name
              });
            });
          }
        });
      }
      
      // Add instant answer if available
      if (data.Answer) {
        formattedResults.unshift({
          type: 'answer',
          content: data.Answer,
          title: 'Instant Answer'
        });
      }
      
      // Add definition if available
      if (data.Definition) {
        formattedResults.push({
          type: 'definition',
          title: 'Definition',
          content: data.Definition,
          url: data.DefinitionURL,
          source: data.DefinitionSource
        });
      }
      
      // If we have an image, add it
      if (data.Image) {
        formattedResults.push({
          type: 'image',
          title: data.Heading || 'Image Result',
          imageUrl: data.Image,
          url: data.AbstractURL || data.FirstURL
        });
      }
      
      // Fallback: If we have no results, create a simulated result
      if (formattedResults.length === 0) {
        // Generate some simulated search results
        formattedResults.push({
          type: 'topic',
          title: `${query} - Wikipedia`,
          content: `Information about ${query} from Wikipedia, the free encyclopedia.`,
          url: `https://en.wikipedia.org/wiki/${encodeURIComponent(query.replace(/\s+/g, '_'))}`
        });
        
        formattedResults.push({
          type: 'topic',
          title: `${query} - Official Website`,
          content: `Visit the official website for ${query}.`,
          url: `https://${query.toLowerCase().replace(/\s+/g, '')}.com`
        });
        
        if (query.includes(' ')) {
          const words = query.split(' ');
          formattedResults.push({
            type: 'topic',
            title: `${words[0]} - Related to ${words.slice(1).join(' ')}`,
            content: `Find information about the relationship between ${words[0]} and ${words.slice(1).join(' ')}.`,
            url: `https://duckduckgo.com/?q=${encodeURIComponent(query)}`
          });
        }
      }
      
      setSearchResults(formattedResults);
    } catch (error) {
      console.error("Error searching DuckDuckGo:", error);
      
      // Create fallback search results since the API failed
      const fallbackResults = [];
      
      // Direct links to major search engines with the query
      fallbackResults.push({
        type: 'error',
        title: 'Search Error',
        content: `Unable to get search results for "${query}". ${error.message}`,
        url: null
      });
      
      // Add direct search engine links
      fallbackResults.push({
        type: 'topic',
        title: `Search for "${query}" on DuckDuckGo`,
        content: `Try viewing the results directly on DuckDuckGo's website.`,
        url: `https://duckduckgo.com/?q=${encodeURIComponent(query)}`
      });
      
      fallbackResults.push({
        type: 'topic',
        title: `Search for "${query}" on Wikipedia`,
        content: `Find information about "${query}" on Wikipedia.`,
        url: `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(query)}`
      });
      
      // Add a few simulated topical results based on the search term
      if (query.toLowerCase().includes('instagram')) {
        fallbackResults.push({
          type: 'topic',
          title: 'Instagram - Official Website',
          content: 'Instagram is a photo and video sharing social networking service owned by Meta Platforms.',
          url: 'https://www.instagram.com/'
        });
        
        fallbackResults.push({
          type: 'topic',
          title: 'Instagram - Download the App',
          content: 'Download Instagram for your device from the app store.',
          url: 'https://www.instagram.com/download/'
        });
      } else if (query.toLowerCase().includes('youtube')) {
        fallbackResults.push({
          type: 'topic',
          title: 'YouTube - Official Website',
          content: 'YouTube is an online video sharing and social media platform owned by Google.',
          url: 'https://www.youtube.com/'
        });
      } else if (query.toLowerCase().includes('facebook')) {
        fallbackResults.push({
          type: 'topic',
          title: 'Facebook - Official Website',
          content: 'Facebook is a social media and social networking service owned by Meta Platforms.',
          url: 'https://www.facebook.com/'
        });
      }
      
      setSearchResults(fallbackResults);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle tab click
  const handleTabClick = (tabId) => {
    setActiveTabId(tabId);
  };

  // Add a new tab
  const addNewTab = () => {
    const newTab = {
      id: nextTabId,
      url: "duckduckgo://search",
      title: 'DuckDuckGo Search',
      favicon: null,
      loading: false,
      error: null,
      isCustomSearch: true
    };
    setTabs([...tabs, newTab]);
    setActiveTabId(nextTabId);
    setNextTabId(nextTabId + 1);
    setInputUrl("duckduckgo://search");
    
    // Focus the URL input when creating a new tab
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 100);
  };

  // Close a tab
  const closeTab = (tabId, e) => {
    e.stopPropagation();
    
    const updatedTabs = tabs.filter(tab => tab.id !== tabId);
    
    // If we're closing the active tab, switch to another tab
    if (tabId === activeTabId && updatedTabs.length > 0) {
      // Find the index of the tab being closed
      const closedTabIndex = tabs.findIndex(tab => tab.id === tabId);
      // Select the tab to the left, or if there isn't one, the tab to the right
      const newActiveTab = updatedTabs[closedTabIndex > 0 ? closedTabIndex - 1 : 0];
      setActiveTabId(newActiveTab.id);
    }
    
    setTabs(updatedTabs);
    
    // If closing the last tab, add a new one
    if (updatedTabs.length === 0) {
      addNewTab();
    }
  };
  
  // Update a specific tab with new properties
  const updateTab = (tabId, updates) => {
    setTabs(prevTabs => 
      prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, ...updates } : tab
      )
    );
  };

  // Handle URL input changes
  const handleUrlInput = (e) => {
    setInputUrl(e.target.value);
  };

  // Update handleUrlSubmit to add to history
  const handleUrlSubmit = (e) => {
    e.preventDefault();
    
    let processedUrl = inputUrl.trim();
    
    // If the URL doesn't start with http:// or https://, add https://
    if (!/^(https?:\/\/|duckduckgo:\/\/)/.test(processedUrl)) {
      // Check if it's a URL or a search term
      if (/\.[a-z]{2,}(\/|$)/i.test(processedUrl)) {
        processedUrl = `https://${processedUrl}`;
      } else {
        // Search using the custom DuckDuckGo search
        processedUrl = `duckduckgo://search?q=${encodeURIComponent(processedUrl)}`;
      }
    }
    
    // Update the current tab
    updateTab(activeTabId, { 
      url: processedUrl, 
      loading: processedUrl.startsWith('duckduckgo://search') ? false : true,
      error: null,
      isCustomSearch: processedUrl.startsWith('duckduckgo://search')
    });
    
    // Add to navigation history
    updateHistory(activeTabId, processedUrl);
    
    // If it's a search URL, extract and perform the search
    if (processedUrl.startsWith('duckduckgo://search')) {
      const queryParam = new URLSearchParams(processedUrl.replace('duckduckgo://search', '')).get('q');
      if (queryParam) {
        setSearchQuery(queryParam);
        performDuckDuckGoSearch(queryParam);
      } else {
        setSearchQuery("");
        setSearchResults([]);
      }
    }
    
    setInputUrl(processedUrl);
    setShowSafeWebsites(false);
  };

  // Handle load events from iframe
  const handleLoad = (tabId) => {
    // Use the iframe title as the tab title if possible
    try {
      const iframe = document.getElementById(`browser-frame-${tabId}`);
      if (iframe && iframe.contentDocument) {
        const title = iframe.contentDocument.title;
        if (title) {
          updateTab(tabId, { 
            title: title.length > 20 ? title.substring(0, 20) + '...' : title,
            loading: false
          });
          return;
        }
      }
    } catch (e) {
      // If we can't access the iframe's content due to CORS, just use the domain
      console.log("Could not access iframe content:", e);
    }
    
    // Fallback: Extract domain from URL
    try {
      const url = new URL(tabs.find(tab => tab.id === tabId).url);
      const domain = url.hostname.replace('www.', '');
      updateTab(tabId, { 
        title: domain,
        loading: false
      });
    } catch (e) {
      updateTab(tabId, { loading: false });
    }
  };

  // Handle error events from iframe
  const handleError = (tabId) => {
    const tab = tabs.find(tab => tab.id === tabId);
    if (!tab) return;

    // Attempt to catch and better explain connection issues
    console.error(`Error loading iframe content for tab ${tabId}: ${tab.url}`);

    // Get domain for the error message
    let domain = "the website";
    try {
      const url = new URL(tab.url);
      domain = url.hostname;
    } catch (e) {
      console.error("Error parsing URL:", e);
    }

    // Set a more detailed error message
    updateTab(tabId, { 
      error: `${domain} refused to connect or couldn't be loaded in this frame. This may be due to X-Frame-Options restrictions or network issues.`, 
      loading: false 
    });
  };

  // Update visitSafeWebsite to add to history
  const visitSafeWebsite = (website) => {
    updateTab(activeTabId, { 
      url: website.url, 
      loading: true,
      error: null,
      isCustomSearch: false
    });
    
    // Add to navigation history
    updateHistory(activeTabId, website.url);
    
    setInputUrl(website.url);
    setShowSafeWebsites(false);
  };

  // Open external URL in default browser
  const openExternalUrl = (url) => {
    window.open(url, '_blank');
  };

  // Update handleResultClick to add to history
  const handleResultClick = (result) => {
    if (result.url) {
      // Try to load in our browser first
      updateTab(activeTabId, {
        url: result.url,
        loading: true,
        error: null,
        isCustomSearch: false
      });
      
      // Add to navigation history
      updateHistory(activeTabId, result.url);
      
      setInputUrl(result.url);
    }
  };

  const activeTab = tabs.find(tab => tab.id === activeTabId) || tabs[0];

  // Render custom search results
  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '15px' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#4a90e2' }}></i>
            </div>
            <div>Searching for "{searchQuery}"...</div>
          </div>
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '20px'
        }}>
          {/* Logo and centered search similar to Google */}
          <div style={{ marginBottom: '30px', marginTop: '-80px' }}>
            <img 
              src="https://duckduckgo.com/assets/logo_homepage.normal.v108.svg" 
              alt="DuckDuckGo Logo" 
              style={{ width: '200px' }}
            />
          </div>
          
          <div style={{ width: '100%', maxWidth: '584px' }}>
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const searchInput = document.getElementById('centered-search-input');
                if (searchInput && searchInput.value) {
                  const newUrl = `duckduckgo://search?q=${encodeURIComponent(searchInput.value)}`;
                  updateTab(activeTabId, { 
                    url: newUrl, 
                    loading: false,
                    error: null,
                    isCustomSearch: true
                  });
                  // Add to history
                  updateHistory(activeTabId, newUrl);
                  setInputUrl(newUrl);
                  setSearchQuery(searchInput.value);
                  performDuckDuckGoSearch(searchInput.value);
                }
              }}
              style={{ 
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              <div style={{
                display: 'flex',
                width: '100%',
                border: '1px solid #dfe1e5',
                borderRadius: '24px',
                padding: '0 8px',
                height: '44px',
                boxShadow: '0 1px 6px rgba(32,33,36,.28)',
                backgroundColor: 'white',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <div style={{ padding: '0 13px' }}>
                  <i className="fas fa-search" style={{ color: '#9aa0a6' }}></i>
                </div>
                <input 
                  id="centered-search-input"
                  type="text"
                  defaultValue=""
                  onChange={(e) => e.target.value = e.target.value}
                  style={{
                    flex: 1,
                    height: '34px',
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                    color: '#000'
                  }}
                  placeholder="Search the web without being tracked"
                  autoFocus
                />
                <div style={{ padding: '0 8px' }}>
                  <button 
                    type="button" 
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-microphone" style={{ color: '#4285f4' }}></i>
                  </button>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button 
                  type="submit"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #f8f9fa',
                    borderRadius: '4px',
                    color: '#3c4043',
                    padding: '0 16px',
                    height: '36px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 1px rgba(0,0,0,.1)';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.border = '1px solid #dadce0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.border = '1px solid #f8f9fa';
                  }}
                >
                  Search
                </button>
                <button 
                  type="button"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #f8f9fa',
                    borderRadius: '4px',
                    color: '#3c4043',
                    padding: '0 16px',
                    height: '36px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 1px rgba(0,0,0,.1)';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.border = '1px solid #dadce0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.border = '1px solid #f8f9fa';
                  }}
                  onClick={handleFeelingLucky}
                >
                  I'm Feeling Lucky
                </button>
              </div>
            </form>
          </div>
          
          <div style={{ 
            color: '#666', 
            maxWidth: '600px', 
            margin: '40px auto 0', 
            textAlign: 'center',
            fontSize: '13px'
          }}>
            <p>DuckDuckGo API doesn't track you. That's our privacy policy in a nutshell.</p>
            <p style={{ marginTop: '10px' }}>
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  visitSafeWebsite(safeWebsites.find(s => s.name === "DuckDuckGo") || safeWebsites[0]);
                }}
                style={{ color: '#4285f4', textDecoration: 'none' }}
              >
                Visit DuckDuckGo.com
              </a>
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', overflowY: 'auto', height: '100%' }}>
        {/* Search header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px', 
          padding: '10px' 
        }}>
          <img 
            src="https://duckduckgo.com/assets/logo_header.v108.svg" 
            alt="DuckDuckGo"
            style={{ height: '30px', marginRight: '20px' }}
          />
          <div style={{ 
            display: 'flex',
            flex: 1,
            border: '1px solid #dfe1e5',
            borderRadius: '24px',
            padding: '0 8px',
            height: '40px',
            alignItems: 'center',
            backgroundColor: 'white'
          }}>
            <div style={{ padding: '0 8px' }}>
              <i className="fas fa-search" style={{ color: '#9aa0a6' }}></i>
            </div>
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newUrl = `duckduckgo://search?q=${encodeURIComponent(searchQuery)}`;
                  updateTab(activeTabId, { 
                    url: newUrl, 
                    loading: false,
                    error: null,
                    isCustomSearch: true
                  });
                  // Add to history
                  updateHistory(activeTabId, newUrl);
                  setInputUrl(newUrl);
                  performDuckDuckGoSearch(searchQuery);
                }
              }}
              style={{
                flex: 1,
                height: '34px',
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                color: '#000'
              }}
            />
            {searchQuery && (
              <button 
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  const newUrl = 'duckduckgo://search';
                  updateTab(activeTabId, { 
                    url: newUrl, 
                    title: 'DuckDuckGo Search',
                    loading: false,
                    error: null,
                    isCustomSearch: true
                  });
                  // Add to history
                  updateHistory(activeTabId, newUrl);
                  setInputUrl(newUrl);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0 8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#70757a'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {searchResults.map((result, index) => (
          <div 
            key={index}
            style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              borderRadius: '8px',
              backgroundColor: result.type === 'answer' ? '#f0f7ff' : 'white',
              border: '1px solid #e0e0e0',
              cursor: result.url ? 'pointer' : 'default'
            }}
            onClick={() => result.url && handleResultClick(result)}
          >
            {result.type === 'answer' && (
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: '#4a90e2' }}>
                {result.title}
              </div>
            )}
            
            {result.type === 'image' ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  {result.title}
                </div>
                <img 
                  src={result.imageUrl} 
                  alt={result.title}
                  style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <>
                <div style={{ 
                  fontSize: result.type === 'abstract' ? '20px' : '16px', 
                  fontWeight: 'bold',
                  marginBottom: '5px',
                  color: result.url ? '#1a0dab' : '#333'
                }}>
                  {result.title}
                </div>
                
                {result.url && (
                  <div style={{ fontSize: '14px', color: '#006621', marginBottom: '5px' }}>
                    {result.url}
                  </div>
                )}
                
                <div style={{ fontSize: '14px', color: '#333' }}>
                  {result.content}
                </div>
                
                {result.source && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Source: {result.source}
                  </div>
                )}
                
                {result.category && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Category: {result.category}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        
        <div style={{ textAlign: 'center', margin: '30px 0', color: '#666', fontSize: '12px' }}>
          <p>Results provided by DuckDuckGo API</p>
          <button 
            onClick={() => openExternalUrl(`https://duckduckgo.com/?q=${encodeURIComponent(searchQuery)}`)}
            style={{
              backgroundColor: '#4a90e2',
              color: 'white',
              border: 'none',
              padding: '8px 15px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            View on DuckDuckGo.com
          </button>
        </div>
      </div>
    );
  };

  // Handle click on I'm Feeling Lucky button
  const handleFeelingLucky = () => {
    // Choose a random website from the safe websites list
    const randomSite = safeWebsites[Math.floor(Math.random() * safeWebsites.length)];
    
    // Update tab with random site
    updateTab(activeTabId, {
      url: randomSite.url,
      loading: true,
      error: null,
      isCustomSearch: false
    });
    
    // Update URL in address bar
    setInputUrl(randomSite.url);
    setShowSafeWebsites(false);
  };

  return (
    <div style={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'black', 
      color: 'white'
    }}>
      {/* Browser Navigation Bar */}
      <div style={{
        display: 'flex',
        padding: '8px 10px',
        backgroundColor: '#111',
        borderBottom: '1px solid #333'
      }}>
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: canGoBack() ? '#ccc' : '#555',
            padding: '0 8px',
            fontSize: '16px',
            cursor: canGoBack() ? 'pointer' : 'default',
            opacity: canGoBack() ? 1 : 0.5
          }}
          title="Back"
          onClick={goBack}
          disabled={!canGoBack()}
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: canGoForward() ? '#ccc' : '#555',
            padding: '0 8px',
            fontSize: '16px',
            cursor: canGoForward() ? 'pointer' : 'default',
            opacity: canGoForward() ? 1 : 0.5
          }}
          title="Forward"
          onClick={goForward}
          disabled={!canGoForward()}
        >
          <i className="fas fa-arrow-right"></i>
        </button>
        
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: '#ccc',
            padding: '0 8px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
          title="Refresh"
          onClick={() => {
            const tab = tabs.find(tab => tab.id === activeTabId);
            if (tab) {
              if (tab.isCustomSearch) {
                // Re-run the search
                const queryParam = new URLSearchParams(tab.url.replace('duckduckgo://search', '')).get('q');
                if (queryParam) {
                  performDuckDuckGoSearch(queryParam);
                  // Update tab title to reflect search
                  updateTab(activeTabId, { 
                    title: `${queryParam} - DuckDuckGo Search`,
                    loading: false
                  });
                } else {
                  // Reset to home page if no query
                  setSearchQuery('');
                  setSearchResults([]);
                  updateTab(activeTabId, { 
                    title: 'DuckDuckGo Search',
                    loading: false
                  });
                }
              } else {
                // Regular refresh for iframe content
                updateTab(activeTabId, { loading: true, error: null });
                
                try {
                  const iframe = document.getElementById(`browser-frame-${activeTabId}`);
                  if (iframe) {
                    iframe.src = tab.url;
                  }
                } catch (e) {
                  console.log("Could not refresh:", e);
                  updateTab(activeTabId, { 
                    error: "Could not refresh the page. " + e.message,
                    loading: false
                  });
                }
              }
            }
          }}
        >
          <i className="fas fa-sync"></i>
        </button>
        
        {/* URL Input Bar */}
        <form 
          style={{ flex: 1, margin: '0 8px', position: 'relative' }}
          onSubmit={handleUrlSubmit}
        >
          <input
            ref={inputRef}
            type="text"
            value={inputUrl}
            onChange={handleUrlInput}
            onFocus={() => setShowSafeWebsites(true)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: '15px',
              border: '1px solid #333',
              backgroundColor: '#222',
              color: 'white',
              fontSize: '14px'
            }}
            placeholder="Search DuckDuckGo or enter URL"
          />
          
          {/* Safe websites dropdown */}
          {showSafeWebsites && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#222',
              border: '1px solid #333',
              borderRadius: '8px',
              marginTop: '5px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              zIndex: 1000
            }}>
              <div style={{ padding: '10px 15px', borderBottom: '1px solid #333' }}>
                <p style={{ margin: 0, color: '#aaa', fontSize: '13px' }}>
                  Many popular sites (like Google) block being loaded in frames.
                  Try these sites that work:
                </p>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {safeWebsites.map((site, index) => (
                  <div 
                    key={index}
                    onClick={() => visitSafeWebsite(site)}
                    style={{
                      padding: '8px 15px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: index < safeWebsites.length - 1 ? '1px solid #333' : 'none'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#333'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-globe" style={{ marginRight: '10px', color: '#4a90e2' }}></i>
                    {site.name} <span style={{ color: '#888', marginLeft: '5px', fontSize: '12px' }}>({new URL(site.url).hostname})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
        
        <button 
          style={{ 
            backgroundColor: 'transparent', 
            border: 'none',
            color: '#4a90e2',
            padding: '0 8px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
          title="Sites that work"
          onClick={() => setShowSafeWebsites(!showSafeWebsites)}
        >
          <i className="fas fa-shield-alt"></i>
        </button>
      </div>
      
      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        backgroundColor: '#111',
        borderBottom: '1px solid #333',
        overflowX: 'auto',
        height: '36px'
      }}>
        {tabs.map(tab => (
          <div 
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 15px',
              height: '36px',
              minWidth: '100px',
              maxWidth: '200px',
              borderRight: '1px solid #333',
              backgroundColor: tab.id === activeTabId ? '#222' : '#111',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              userSelect: 'none'
            }}
          >
            {tab.loading ? (
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px', fontSize: '14px' }}></i>
            ) : tab.isCustomSearch ? (
              <i className="fas fa-search" style={{ marginRight: '8px', fontSize: '14px' }}></i>
            ) : tab.favicon ? (
              <img src={tab.favicon} alt="" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
            ) : (
              <i className="fas fa-globe" style={{ marginRight: '8px', fontSize: '14px' }}></i>
            )}
            
            <span style={{ 
              flex: 1, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#ccc',
              fontSize: '13px'
            }}>
              {tab.title}
            </span>
            
            <button
              onClick={(e) => closeTab(tab.id, e)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                marginLeft: '8px',
                padding: '0',
                fontSize: '12px',
                width: '20px',
                height: '20px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#444'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        ))}
        
        {/* New Tab Button */}
        <button
          onClick={addNewTab}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '0 15px',
            color: '#888',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
          title="New Tab"
        >
          <i className="fas fa-plus"></i>
        </button>
      </div>
      
      {/* Content Area */}
      <div style={{
        flex: 1,
        position: 'relative',
        backgroundColor: 'white'
      }}>
        {tabs.map(tab => (
          <div 
            key={tab.id} 
            style={{ 
              display: tab.id === activeTabId ? 'block' : 'none',
              height: '100%',
              width: '100%'
            }}
          >
            {tab.loading && !tab.error && (
              <div style={{ 
                position: 'absolute', 
                top: '0', 
                left: '0', 
                right: '0',
                height: '3px'
              }}>
                <div 
                  style={{
                    height: '100%',
                    width: '20%',
                    backgroundColor: '#4a90e2',
                    borderRadius: '2px',
                    animation: 'browser-loading 1.5s infinite ease-in-out',
                    boxShadow: '0 0 10px rgba(74, 144, 226, 0.5)'
                  }}
                ></div>
              </div>
            )}
            
            {tab.error ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#ff6b6b',
                padding: '20px',
                textAlign: 'center'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
                <h3>Connection Error</h3>
                <p>{tab.error}</p>
                <p style={{ color: '#aaa', marginTop: '10px' }}>URL: {tab.url}</p>
                
                <div style={{ marginTop: '30px', maxWidth: '500px' }}>
                  <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '15px', textAlign: 'left' }}>
                    <b>Why this happens:</b> For security reasons, many websites like Google, YouTube, and Facebook 
                    prevent being loaded inside frames in other websites (this is called X-Frame-Options or CORS).
                  </p>
                  
                  <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                    <p style={{ color: '#aaa', fontSize: '14px' }}>
                      <b>Try these sites that work:</b>
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '10px',
                      marginTop: '10px'
                    }}>
                      {safeWebsites.map((site, index) => (
                        <button
                          key={index}
                          onClick={() => visitSafeWebsite(site)}
                          style={{
                            backgroundColor: '#333',
                            border: 'none',
                            borderRadius: '5px',
                            padding: '8px 12px',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <i className="fas fa-globe"></i>
                          {site.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : tab.isCustomSearch ? (
              // Render DuckDuckGo custom search
              <div style={{ 
                height: '100%', 
                width: '100%', 
                backgroundColor: 'white', 
                color: 'black',
                overflowY: 'auto'
              }}>
                {renderSearchResults()}
              </div>
            ) : (
              <iframe 
                id={`browser-frame-${tab.id}`}
                src={tab.url} 
                title={`Web Browser Tab ${tab.id}`}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  border: 'none',
                  backgroundColor: 'white'
                }}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-modals"
                loading="lazy"
                referrerPolicy="no-referrer"
                onLoad={() => handleLoad(tab.id)}
                onError={() => handleError(tab.id)}
              />
            )}
          </div>
        ))}
      </div>
      
      {/* Add a style tag for animations */}
      <style>
        {`
          @keyframes browser-loading {
            0% { transform: translateX(0); }
            50% { transform: translateX(400%); }
            100% { transform: translateX(0); }
          }
        `}
      </style>
    </div>
  );
};

const Window = ({ app, closeApp, theme, setContextMenu, bringToFront, zIndex, isMobile, defaultPosition, defaultSize }) => {
  // State for window position and size
  const [position, setPosition] = useState(defaultPosition || { x: 100, y: 100 });
  const [size, setSize] = useState(
    isMobile 
      ? { width: window.innerWidth * 0.95, height: window.innerHeight * 0.7 }
      : (defaultSize || { width: 800, height: 600 })
  );
  
  // Add state for window minimization and animation
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [animationTarget, setAnimationTarget] = useState({ x: 0, y: 0 });
  
  // Mobile optimization effect - adjust window on resize/orientation change
  useEffect(() => {
    if (!isMobile) return;
    
    const handleResize = () => {
      setSize({ 
        width: window.innerWidth * 0.95, 
        height: window.innerHeight * 0.7 
      });
      setPosition({ 
        x: window.innerWidth * 0.025, 
        y: window.innerHeight * 0.1 
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Add event listener for webos-toggle-window event
  useEffect(() => {
    const handleToggleWindow = (e) => {
      if (e.detail.appId === app.id) {
        console.log(`Toggling window for: ${app.id} (${app.component})`);
        
        // Find the taskbar item for this app to get its position for animation
        const findTaskbarItem = () => {
          const taskbarItem = document.querySelector(`[data-component="${app.component}"]`);
          if (taskbarItem) {
            const rect = taskbarItem.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2
            };
          }
          // Fallback to bottom center if taskbar item not found
          return {
            x: window.innerWidth / 2,
            y: window.innerHeight
          };
        };
        
        if (!isMinimized) {
          // Minimize animation
          const target = findTaskbarItem();
          setAnimationTarget(target);
          setIsMinimizing(true);
          
          // After animation completes, actually set to minimized
          setTimeout(() => {
            setIsMinimized(true);
            setIsMinimizing(false);
          }, 300); // Match this with CSS animation duration
        } else {
          // Restore animation
          setIsMinimized(false);
          setIsRestoring(true);
          
          // Bring to front immediately when restoring
          if (bringToFront) {
            bringToFront(app.id);
          }
          
          // Reset restore animation flag after animation completes
          setTimeout(() => {
            setIsRestoring(false);
          }, 300); // Match this with CSS animation duration
        }
      }
    };
    
    window.addEventListener('webos-toggle-window', handleToggleWindow);
    return () => window.removeEventListener('webos-toggle-window', handleToggleWindow);
  }, [app.id, app.component, isMinimized, bringToFront]);

  // Reference for tracking mouse movements
  const mouseRef = useRef({
    startX: 0,
    startY: 0,
    startTop: 0,
    startLeft: 0,
    startWidth: 0,
    startHeight: 0,
    resizing: false,
    moving: false,
    handle: null,
    animationFrame: null,
    finalWidth: null,
    finalHeight: null,
    finalLeft: null,
    finalTop: null,
    windowElement: null,
  });

  // Start moving the window (only on desktop)
  const startWindowDrag = (e) => {
    if (isMobile) return;
    
    // Bring window to front
    if (bringToFront) {
      bringToFront(app.id);
    }
    
    // Get window element
    const windowElement = document.querySelector(`[data-app-id="${app.id}"]`);
    if (!windowElement) return;
    
    // Store window element and initial position
    mouseRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: position.x,
      startTop: position.y,
      moving: true,
      windowElement: windowElement
    };
    
    // Remove transition for smoother dragging
    windowElement.style.transition = 'none';
    
    // Add global mouse listeners
    document.addEventListener('mousemove', handleWindowDrag);
    document.addEventListener('mouseup', stopWindowDrag);
  };

  // Handle drag movement (only on desktop)
  const handleWindowDrag = (e) => {
    if (!mouseRef.current.moving || !mouseRef.current.windowElement) return;
    
    // Cancel any pending animation frame to avoid multiple queued updates
    if (mouseRef.current.animationFrame) {
      cancelAnimationFrame(mouseRef.current.animationFrame);
    }
    
    // Use requestAnimationFrame for smoother animation
    mouseRef.current.animationFrame = requestAnimationFrame(() => {
    const dx = e.clientX - mouseRef.current.startX;
    const dy = e.clientY - mouseRef.current.startY;
    
      // Calculate new position
      const newLeft = mouseRef.current.startLeft + dx;
      const newTop = mouseRef.current.startTop + dy;
      
      // Apply position directly to DOM element for smoother dragging
      mouseRef.current.windowElement.style.left = `${newLeft}px`;
      mouseRef.current.windowElement.style.top = `${newTop}px`;
      
      // Store final position to update React state when done
      mouseRef.current.finalLeft = newLeft;
      mouseRef.current.finalTop = newTop;
    });
  };

  // Stop dragging (only on desktop)
  const stopWindowDrag = () => {
    // Cancel any pending animation frame
    if (mouseRef.current.animationFrame) {
      cancelAnimationFrame(mouseRef.current.animationFrame);
    }
    
    if (mouseRef.current.moving && mouseRef.current.windowElement) {
      // Restore transition for smooth animations after dragging
      mouseRef.current.windowElement.style.transition = 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)';
      
      // Update React state with final position
      setPosition({
        x: mouseRef.current.finalLeft || mouseRef.current.startLeft,
        y: mouseRef.current.finalTop || mouseRef.current.startTop
      });
    }
    
    // Clean up
    mouseRef.current.moving = false;
    mouseRef.current.windowElement = null;
    document.removeEventListener('mousemove', handleWindowDrag);
    document.removeEventListener('mouseup', stopWindowDrag);
  };

  // Start resizing the window (only on desktop)
  const startResize = (e, handle) => {
    if (isMobile) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Bring window to front
    if (bringToFront) {
      bringToFront(app.id);
    }
    
    // Store the initial window element
    const windowElement = document.querySelector(`[data-app-id="${app.id}"]`);
    if (!windowElement) return;
    
    // Store initial values
    mouseRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startLeft: position.x,
      startTop: position.y,
      resizing: true,
      handle: handle,
      windowElement: windowElement // Store reference to DOM element
    };
    
    // Remove transition during resize for better performance
    windowElement.style.transition = 'none';
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);
  };

  // Handle resize movement (only on desktop)
  const handleResize = (e) => {
    if (!mouseRef.current.resizing || !mouseRef.current.windowElement) return;
    
    // Cancel any pending animation frame
    if (mouseRef.current.animationFrame) {
      cancelAnimationFrame(mouseRef.current.animationFrame);
    }
    
    // Use requestAnimationFrame for smoother resizing
    mouseRef.current.animationFrame = requestAnimationFrame(() => {
    const dx = e.clientX - mouseRef.current.startX;
    const dy = e.clientY - mouseRef.current.startY;
    const handle = mouseRef.current.handle;
      const windowElement = mouseRef.current.windowElement;
    
    let newWidth = mouseRef.current.startWidth;
    let newHeight = mouseRef.current.startHeight;
    let newLeft = mouseRef.current.startLeft;
    let newTop = mouseRef.current.startTop;
    
    // Calculate new dimensions based on resize handle
    switch (handle) {
      case 'se':
        newWidth = Math.max(300, mouseRef.current.startWidth + dx);
        newHeight = Math.max(200, mouseRef.current.startHeight + dy);
        break;
      case 'sw':
        newWidth = Math.max(300, mouseRef.current.startWidth - dx);
        newLeft = mouseRef.current.startLeft + dx;
        newHeight = Math.max(200, mouseRef.current.startHeight + dy);
        break;
      case 'ne':
        newWidth = Math.max(300, mouseRef.current.startWidth + dx);
        newHeight = Math.max(200, mouseRef.current.startHeight - dy);
        newTop = mouseRef.current.startTop + dy;
        break;
      case 'nw':
        newWidth = Math.max(300, mouseRef.current.startWidth - dx);
        newHeight = Math.max(200, mouseRef.current.startHeight - dy);
        newLeft = mouseRef.current.startLeft + dx;
        newTop = mouseRef.current.startTop + dy;
        break;
      case 'n':
        newHeight = Math.max(200, mouseRef.current.startHeight - dy);
        newTop = mouseRef.current.startTop + dy;
        break;
      case 's':
        newHeight = Math.max(200, mouseRef.current.startHeight + dy);
        break;
      case 'e':
        newWidth = Math.max(300, mouseRef.current.startWidth + dx);
        break;
      case 'w':
        newWidth = Math.max(300, mouseRef.current.startWidth - dx);
        newLeft = mouseRef.current.startLeft + dx;
        break;
      default:
        break;
    }
    
      // Apply changes directly to the DOM element for smoother resizing
      // This avoids React state updates during the resize operation
      windowElement.style.width = `${newWidth}px`;
      windowElement.style.height = `${newHeight}px`;
      windowElement.style.left = `${newLeft}px`;
      windowElement.style.top = `${newTop}px`;
      
      // Store the final values to update React state when resize ends
      mouseRef.current.finalWidth = newWidth;
      mouseRef.current.finalHeight = newHeight;
      mouseRef.current.finalLeft = newLeft;
      mouseRef.current.finalTop = newTop;
    });
  };

  // Stop resizing (only on desktop) 
  const stopResize = () => {
    // Cancel any pending animation frame
    if (mouseRef.current.animationFrame) {
      cancelAnimationFrame(mouseRef.current.animationFrame);
    }
    
    // Only update React state once after resize is complete
    if (mouseRef.current.resizing && mouseRef.current.windowElement) {
      const windowElement = mouseRef.current.windowElement;
      
      // Restore transition for smooth animations after resizing
      windowElement.style.transition = 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)';
      
      // Update React state with final values
      setSize({ 
        width: mouseRef.current.finalWidth || mouseRef.current.startWidth, 
        height: mouseRef.current.finalHeight || mouseRef.current.startHeight 
      });
      setPosition({ 
        x: mouseRef.current.finalLeft || mouseRef.current.startLeft, 
        y: mouseRef.current.finalTop || mouseRef.current.startTop 
      });
    }
    
    mouseRef.current.resizing = false;
    mouseRef.current.windowElement = null;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
  };
  
  // Convenience functions for resize handles
  const startResizeSE = (e) => startResize(e, 'se');

  // Render the app component based on its type
  const renderComponent = (app) => {
    switch (app.component) {
      case 'Terminal':
        return <Terminal theme={theme} />;
      case 'FileExplorer':
        return <FileExplorer theme={theme} />;
      case 'AIAssistant':
        return <AIAssistant theme={theme} />;
      case 'YouTube':
        return <YouTube theme={theme} />;
      case 'Notepad':
        return <Notepad theme={theme} />;
      case 'Store':
        return <Store theme={theme} addAppToDesktop={window.addAppToDesktop} />;
      case 'Calculator':
        return <Calculator theme={theme} />;
      case 'MusicPlayer':
        return <MusicPlayer theme={theme} />;
      case 'CodeEditor':
        return <CodeEditor theme={theme} />;
      case 'Settings':
        return <Settings 
                  theme={theme} 
                  onThemeChange={app.onThemeChange} 
                  onWallpaperChange={app.onWallpaperChange} 
                />;
      case 'SettingsPlaceholder':
        return <SettingsPlaceholder 
                  theme={theme} 
                  onThemeChange={app.onThemeChange} 
                  onWallpaperChange={app.onWallpaperChange} 
                />;
      case 'NotificationCenter':
        return <NotificationCenter />;
      case 'TicTacToe':
        return <TicTacToe />;
      case 'WebBrowser':
        // Use the imported Browser component with a URL if provided
        return <Browser theme={theme} url={app.url} />;
      case 'Browser':
        // Also handle 'Browser' as a component name for backward compatibility
        return <Browser theme={theme} url={app.url} />;
      case 'Clock':
        return <Clock theme={theme} />;
      default:
        return <div style={{ padding: '20px' }}>App not found: {app.component}</div>;
    }
  };

  // Brand icons for specific apps
  const brandIcons = ['chrome', 'edge', 'spotify', 'facebook', 'twitter', 'windows', 'youtube'];
  
  // Helper function to generate the proper icon class
  const getIconClassName = (iconName) => {
    if (!iconName) return 'fas fa-window-maximize'; // Default icon
    
    // Normalize icon name to lowercase
    const normalizedName = iconName.toLowerCase();
    
    if (brandIcons.includes(normalizedName)) {
      return `fab fa-${normalizedName}`;
    }
    return `fas fa-${normalizedName}`;
  };

  // Function to minimize window with animation
  const minimizeWindow = () => {
    // Find the taskbar item for this app to get its position for animation
    const findTaskbarItem = () => {
      const taskbarItem = document.querySelector(`[data-component="${app.component}"]`);
      if (taskbarItem) {
        const rect = taskbarItem.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
      // Fallback to bottom center if taskbar item not found
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight
      };
    };
    
    const target = findTaskbarItem();
    setAnimationTarget(target);
    setIsMinimizing(true);
    
    // After animation completes, actually set to minimized
    setTimeout(() => {
      setIsMinimized(true);
      setIsMinimizing(false);
    }, 300); // Match this with CSS animation duration
  };

  return (
    <div 
      className="window"
      data-app-id={app.id}
      style={{
        position: 'absolute',
        width: `${size.width}px`,
        height: `${size.height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        backgroundColor: theme === 'dark' ? 'rgba(20, 25, 35, 0.4)' : 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)', /* For Safari */
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
        display: isMinimized ? 'none' : 'flex',
        flexDirection: 'column',
        zIndex: zIndex || 1,
        touchAction: isMobile ? 'none' : 'auto',
        
        // Add animation properties
        transition: 'all 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2)',
        transform: isMinimizing 
          ? `translate(${animationTarget.x - (position.x + size.width/2)}px, ${animationTarget.y - (position.y + size.height/2)}px) scale(0.1)` 
          : isRestoring 
            ? 'scale(1)' 
            : 'none',
        opacity: isMinimizing ? 0 : 1,
        transformOrigin: isRestoring ? 'center bottom' : 'center center',
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (bringToFront) {
          bringToFront(app.id);
        }
      }}
    >
      {/* Window Title Bar */}
      <div 
        className="window-titlebar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '0 8px' : '0 12px',
          height: isMobile ? '32px' : '36px',
          backgroundColor: theme === 'dark' ? 'rgba(20, 25, 35, 0.3)' : 'rgba(240, 245, 250, 0.3)',
          WebkitBackdropFilter: 'blur(8px)', /* For Safari */
          backdropFilter: 'blur(8px)',
          borderBottom: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          cursor: isMobile ? 'default' : 'move',
          userSelect: 'none',
          transition: 'all 0.3s ease',
        }}
        onMouseDown={!isMobile ? startWindowDrag : undefined}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (setContextMenu) {
            setContextMenu({
              x: e.clientX,
              y: e.clientY,
              actions: {
                maximize: () => {
                  console.log("Maximize window");
                  setContextMenu(null);
                },
                minimize: () => {
                  console.log("Minimize window");
                  minimizeWindow();
                  setContextMenu(null);
                },
                close: () => {
                  closeApp(app.id);
                  setContextMenu(null);
                }
              }
            });
          }
        }}
      >
        {/* Window Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          overflow: 'hidden',
          maxWidth: '70%',
        }}>
          <div style={{ 
            fontSize: isMobile ? '16px' : '18px',
            width: isMobile ? '24px' : '26px',
            height: isMobile ? '24px' : '26px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '8px',
            color: theme === 'dark' ? 'white' : '#333',
          }}>
            <i className={app.className || (app.icon ? getIconClassName(app.icon) : 'fas fa-window-maximize')} />
          </div>
          <div style={{ 
            color: theme === 'dark' ? '#f8f9fa' : '#212529',
            fontWeight: 500,
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {app.title || app.name}
          </div>
        </div>
        
        {/* Windows 10/11 Style Window Controls */}
        <div 
          className="window-titlebar-buttons"
          style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginRight: '-10px'
          }}
        >
          {/* Minimize Button - Windows style */}
          <div 
            className="window-titlebar-button"
            style={{
              width: '46px',
              height: isMobile ? '32px' : '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log("Minimize window");
              minimizeWindow();
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div style={{ width: '10px', height: '1px', backgroundColor: theme === 'dark' ? 'white' : 'black' }}></div>
          </div>
          
          {/* Maximize Button - Only on desktop - Windows style */}
          {!isMobile && (
            <div 
              className="window-titlebar-button"
              style={{
                width: '46px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={(e) => {
                e.stopPropagation();
                console.log("Maximize/restore window");
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ 
                width: '10px', 
                height: '10px', 
                border: `1px solid ${theme === 'dark' ? 'white' : 'black'}`,
                boxSizing: 'border-box'
              }}></div>
            </div>
          )}
          
          {/* Close Button - Windows style */}
          <div 
            className="window-titlebar-button"
            style={{
              width: '46px',
              height: isMobile ? '32px' : '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              borderTopRightRadius: '8px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              closeApp(app.id);
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e81123'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div style={{ 
              width: '12px', 
              height: '12px', 
              position: 'relative',
              transform: 'rotate(45deg)'
            }}>
              <div style={{ 
                position: 'absolute', 
                width: '100%', 
                height: '1px', 
                backgroundColor: theme === 'dark' ? 'white' : 'black',
                top: '50%',
                left: 0,
                transform: 'translateY(-50%)'
              }}></div>
              <div style={{ 
                position: 'absolute', 
                width: '1px', 
                height: '100%', 
                backgroundColor: theme === 'dark' ? 'white' : 'black',
                left: '50%',
                top: 0,
                transform: 'translateX(-50%)'
              }}></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Window Content - glass effect */}
      <div 
        className="window-content"
        style={{
          height: 'calc(100% - 36px)', // Subtract the title bar height
          overflow: 'hidden',
          backgroundColor: theme === 'dark' ? 'rgba(20, 25, 35, 0.2)' : 'rgba(245, 245, 245, 0.2)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)', /* For Safari */
          padding: '10px',
          transition: 'all 0.3s ease',
        }}
      >
        {renderComponent(app)}
      </div>

      {!isMobile && (
        <>
          {/* Corner resize handles */}
          <div 
            className="resize-handle resize-handle-nw" 
            onMouseDown={(e) => startResize(e, 'nw')}
            style={{
              position: 'absolute',
              top: '-3px',
              left: '-3px',
              width: '12px',
              height: '12px',
              cursor: 'nwse-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-ne" 
            onMouseDown={(e) => startResize(e, 'ne')}
            style={{
              position: 'absolute',
              top: '-3px',
              right: '-3px',
              width: '12px',
              height: '12px',
              cursor: 'nesw-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-sw" 
            onMouseDown={(e) => startResize(e, 'sw')}
            style={{
              position: 'absolute',
              bottom: '-3px',
              left: '-3px',
              width: '12px',
              height: '12px',
              cursor: 'nesw-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-se" 
            onMouseDown={(e) => startResize(e, 'se')}
            style={{
              position: 'absolute',
              bottom: '-3px',
              right: '-3px',
              width: '12px',
              height: '12px',
              cursor: 'nwse-resize',
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            }}
          />
          
          {/* Edge resize handles */}
          <div 
            className="resize-handle resize-handle-n" 
            onMouseDown={(e) => startResize(e, 'n')}
            style={{
              position: 'absolute',
              top: '-3px',
              left: '12px',
              right: '12px',
              height: '6px',
              cursor: 'ns-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-e" 
            onMouseDown={(e) => startResize(e, 'e')}
            style={{
              position: 'absolute',
              top: '12px',
              right: '-3px',
              width: '6px',
              bottom: '12px',
              cursor: 'ew-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-s" 
            onMouseDown={(e) => startResize(e, 's')}
            style={{
              position: 'absolute',
              bottom: '-3px',
              left: '12px',
              right: '12px',
              height: '6px',
              cursor: 'ns-resize',
              zIndex: 10,
            }}
          />
          <div 
            className="resize-handle resize-handle-w" 
            onMouseDown={(e) => startResize(e, 'w')}
            style={{
              position: 'absolute',
              top: '12px',
              left: '-3px',
              width: '6px',
              bottom: '12px',
              cursor: 'ew-resize',
              zIndex: 10,
            }}
          />
        </>
      )}
    </div>
  );
};

export default Window; 